'use client'

import { useCallback, useMemo, useState, JSX } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { groupBy, uniq } from 'lodash'
import { base, mainnet, optimism } from 'viem/chains'

import { isSimulating } from '~/app/settings/team/banks/BanksSimulator'
import { SplitsLink as Link } from '~/components/Link'
import {
  AddressDisplay,
  Copyable,
  Group,
  LabelValue,
  Message,
  SelectInput,
  SmartAccountSelectInput,
  TokenDisplay,
} from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import {
  BASE_EURC_ADDRESS,
  BASE_USDC_ADDRESS,
  BRIDGE_FEE_PERCENT,
  ETHEREUM_USDC_ADDRESS,
  OPTIMISM_USDC_ADDRESS,
} from '~/constants/addresses'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgUpdates, useRequiredActiveOrg } from '~/hooks/org'
import { useMicrodeposits } from '~/hooks/useFiat'
import { useServerRequests } from '~/hooks/useRequests'
import {
  BridgeVirtualAccount,
  BridgeVirtualAccountSourceData,
  IAddress,
} from '~/types'

// Re-export for backwards compatibility
export { BRIDGE_FEE_PERCENT as DEVELOPER_FEE_PERCENT }

type VirtualAccountTokenOption = {
  token: IAddress
  chainId: number
  label: string
}

type PendingChange = {
  address: string
  token: VirtualAccountTokenOption
}

export const VirtualAccountDepositInstructions = ({
  currency,
}: {
  currency?: string
}): JSX.Element => {
  const paymentMethod =
    currency === 'eur'
      ? 'a SEPA transfer'
      : currency === 'usd'
        ? 'a wire or ACH'
        : 'a bank transfer'

  return (
    <div>
      Send funds via {paymentMethod} to the bank account below. We charge{' '}
      {BRIDGE_FEE_PERCENT}% on all deposits to cover costs. Funds should arrive
      within 1 business day. For more info on settlement,{' '}
      <Link
        className="underline"
        target="tab"
        path="https://apidocs.bridge.xyz/platform/orchestration/more/cutoffs#processing-windows-and-cutoff-times"
      >
        click here
      </Link>
      .
    </div>
  )
}

export const VirtualAccount = ({
  virtualAccount,
  withForm = true,
}: {
  withForm?: boolean
  virtualAccount: BridgeVirtualAccount
}): JSX.Element => {
  const org = useRequiredActiveOrg()

  const { microdeposits } = useMicrodeposits()
  const groupedMicrodeposits = useMemo(() => {
    return groupBy(microdeposits, 'name')
  }, [microdeposits])
  const queryClient = useQueryClient()
  const { serverPostRequest: dismissMicrodeposits } = useServerRequests()

  const canDeposit = org.smartAccountsChainIds.some((id) =>
    VIRTUAL_ACCOUNT_CHAIN_IDS.includes(id),
  )

  return (
    <div>
      {microdeposits.length > 0 &&
        Object.entries(groupedMicrodeposits).map(([name, deposits], index) => (
          <Message
            key={`microdeposit-group-${index}`}
            action={{
              label: 'Dismiss',
              variant: 'link',
              onClick: async () => {
                await dismissMicrodeposits({
                  path: '/api/org/bridge/microdeposits/dismiss',
                  params: {
                    orgId: org.id,
                    microdepositIds: deposits.map(
                      ({ microdepositId }) => microdepositId,
                    ),
                  },
                  errorMessage: 'Failed to dismiss micro deposits',
                })

                queryClient.invalidateQueries({
                  queryKey: [QUERY_KEYS.MICRODEPOSITS, org.id],
                })
              },
            }}
          >
            <>
              <span>
                Microdeposit verification: {name} will ask you to provide the
                following amounts:&nbsp;
              </span>
              {deposits.map(({ amount, microdepositId }, index) => (
                <span key={`microdeposit-${microdepositId}`}>
                  {`$${amount}`} (#{index + 1})
                  {index < deposits.length - 1 ? ', ' : ''}
                </span>
              ))}
            </>
          </Message>
        ))}
      {canDeposit && (
        <div className="flex flex-col space-y-8">
          <VirtualAccountBankDetails
            virtualAccountSourceData={virtualAccount.sourceData}
          />
          {withForm ? (
            <VirtualAccountForm
              key={virtualAccount.id}
              virtualAccount={virtualAccount}
            />
          ) : (
            <VirtualAccountInfo virtualAccount={virtualAccount} />
          )}
        </div>
      )}
    </div>
  )
}

const VirtualAccountInfo = ({
  virtualAccount,
}: {
  virtualAccount: BridgeVirtualAccount
}): JSX.Element => {
  const virtualAccountToken = virtualAccount?.destinationToken
  const virtualAccountChainId = virtualAccount?.destinationChainId
  const virtualAccountAddress = virtualAccount?.destinationAddress

  return (
    <div>
      <p>
        After depositing funds, they will become available in{' '}
        <span className="inline-flex space-x-1 align-bottom">
          <AddressDisplay
            address={virtualAccountAddress}
            chainId={virtualAccountChainId}
            layout="inline-text"
          />
          <div className="inline">as</div>
          <TokenDisplay
            address={virtualAccountToken}
            chainId={virtualAccountChainId}
            layout="inline-text"
          />
        </span>
        . To modify this, go to{' '}
        <Link className="inline-flex underline" path="/settings/team/banks">
          <div>Settings</div>
        </Link>
        .
      </p>
    </div>
  )
}

const VirtualAccountForm = ({
  virtualAccount,
}: {
  virtualAccount: BridgeVirtualAccount
}): JSX.Element => {
  const org = useRequiredActiveOrg()
  const { serverPutRequest } = useServerRequests()
  const { replaceBridgeVirtualAccount } = useActiveOrgUpdates()
  const { setGlobalModalState } = useAuth()

  const virtualAccountTokensByCurrency =
    virtualAccount.sourceData.currency === 'eur'
      ? EU_VIRTUAL_ACCOUNT_TOKENS
      : US_VIRTUAL_ACCOUNT_TOKENS

  // Calculate virtualAccountTokens first to use for initializing selectedToken state
  const eligibleTokens = virtualAccountTokensByCurrency.filter(({ chainId }) =>
    org.smartAccountsChainIds.includes(chainId),
  )
  const virtualAccountTokens =
    eligibleTokens.length > 0
      ? eligibleTokens
      : [virtualAccountTokensByCurrency[0]]

  // Find the initial token object from the available virtualAccountTokens
  const initialSelectedTokenObject =
    virtualAccountTokens.find(
      (vt) =>
        vt.token === virtualAccount.destinationToken &&
        vt.chainId === virtualAccount.destinationChainId,
    ) || virtualAccountTokens[0] // Fallback to the first available option

  const [selectedSmartAccountAddress, setSelectedSmartAccountAddress] =
    useState(virtualAccount.destinationAddress)
  const [selectedToken, setSelectedToken] = useState(initialSelectedTokenObject)

  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null)

  const save = useCallback(
    async (address: string, token: VirtualAccountTokenOption) => {
      if (isSimulating()) return
      // Server returns a fresh VA (destination changes are deactivate + create).
      const result = await serverPutRequest<BridgeVirtualAccount>({
        path: `/api/org/bridge/virtual_accounts/${virtualAccount.id}`,
        params: {
          orgId: org.id,
          address,
          tokenAddress: token.token,
          tokenChainId: token.chainId,
          // Signals to the BE that this client knows the save rotates bank
          // details. Stale clients without this flag get a 4xx asking the
          // user to refresh.
          confirmRotateBankDetails: true,
        },
        successMessage: 'Deposit settings updated',
        errorMessage: 'Failed to update settings',
      })
      if (result) replaceBridgeVirtualAccount(virtualAccount.id, result.data)
    },
    [org.id, virtualAccount.id, serverPutRequest, replaceBridgeVirtualAccount],
  )

  const openConfirmation = useCallback(
    (change: PendingChange) => {
      setPendingChange(change)
      const onConfirm = async () => {
        setSelectedSmartAccountAddress(change.address)
        setSelectedToken(change.token)
        try {
          await save(change.address, change.token)
        } finally {
          setPendingChange(null)
        }
      }
      setGlobalModalState({
        isOpen: true,
        content: (
          <VirtualAccountRotateDialog
            isEur={virtualAccount.sourceData.currency === 'eur'}
            onConfirm={onConfirm}
          />
        ),
        onDismiss: () => setPendingChange(null),
      })
    },
    [save, setGlobalModalState, virtualAccount.sourceData.currency],
  )

  const displayedAddress = pendingChange?.address ?? selectedSmartAccountAddress
  const displayedToken = pendingChange?.token ?? selectedToken

  return (
    <div className="grid w-full grid-cols-2 gap-2">
      <SmartAccountSelectInput
        label="Deposit to"
        selectedOption={displayedAddress}
        setValue={(value) => {
          if (value === selectedSmartAccountAddress) return
          openConfirmation({ address: value, token: selectedToken })
        }}
        includeAutomations
        hideBalance
        addressLayout="standard"
      />
      <SelectInput
        selectedOption={displayedToken}
        label="Receive as"
        options={virtualAccountTokens.map((token) => ({
          value: token,
          label: (
            <TokenDisplay
              address={token.token}
              chainId={token.chainId}
              layout="standard"
            />
          ),
        }))}
        placeholder="Select a token"
        selectValue={(token) => {
          if (
            token.token === selectedToken.token &&
            token.chainId === selectedToken.chainId
          )
            return
          openConfirmation({ address: selectedSmartAccountAddress, token })
        }}
      />
    </div>
  )
}

const VirtualAccountRotateDialog = ({
  isEur,
  onConfirm,
}: {
  isEur: boolean
  onConfirm: () => Promise<void>
}): JSX.Element => {
  const { closeGlobalModal } = useAuth()
  const [isSaving, setIsSaving] = useState(false)

  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      await onConfirm()
    } finally {
      setIsSaving(false)
      closeGlobalModal()
    }
  }

  return (
    <SplitsDialogContent
      id="virtualAccountRotateDialog"
      title="Update deposit settings?"
      hideTitle={false}
      variant="cta"
      isLoading={isSaving}
      actionButton={{ text: 'Update', onClick: handleConfirm }}
      cancelButton={{ text: 'Cancel' }}
    >
      <p>
        This will result in{' '}
        {isEur ? 'a new IBAN and BIC' : 'new routing and account numbers'} for
        all future bank deposits. Any funds you send to the old account will be
        returned to sender.
      </p>
    </SplitsDialogContent>
  )
}

export const VirtualAccountBankDetails = ({
  virtualAccountSourceData,
}: {
  virtualAccountSourceData: BridgeVirtualAccountSourceData
}) => {
  const isEUVirtualAccount = virtualAccountSourceData.currency === 'eur'
  const virtualAccountNumber = isEUVirtualAccount
    ? virtualAccountSourceData['iban']
    : virtualAccountSourceData['bankAccountNumber']

  return (
    <>
      <div className="grid w-full grid-cols-2 gap-2">
        <Copyable
          layout="field"
          label={isEUVirtualAccount ? 'BIC' : 'Routing number'}
          value={
            isEUVirtualAccount
              ? virtualAccountSourceData['bic']!
              : virtualAccountSourceData['bankRoutingNumber']!
          }
        />
        <Copyable
          layout="field"
          label="Account number"
          value={virtualAccountNumber!}
          truncateMiddle={isEUVirtualAccount ? 4 : undefined}
        />
      </div>
      <Group label="Bank details">
        <div className="flex flex-col gap-3 rounded-xl bg-surface-muted px-3.5 py-2.5">
          {virtualAccountSourceData['bankBeneficiaryName'] && (
            <LabelValue
              label="Beneficiary name"
              value={virtualAccountSourceData['bankBeneficiaryName']}
              copyable
            />
          )}
          <LabelValue
            label="Bank name"
            value={virtualAccountSourceData['bankName']}
            copyable
          />
          <LabelValue
            label="Bank address"
            value={virtualAccountSourceData['bankAddress']}
            copyable
          />
        </div>
      </Group>
    </>
  )
}

const US_VIRTUAL_ACCOUNT_TOKENS: {
  token: IAddress
  chainId: number
  label: string
}[] = [
  {
    token: BASE_USDC_ADDRESS,
    chainId: base.id,
    label: 'USDC on Base',
  },
  {
    token: OPTIMISM_USDC_ADDRESS,
    chainId: optimism.id,
    label: 'USDC on Optimism',
  },
  {
    token: ETHEREUM_USDC_ADDRESS,
    chainId: mainnet.id,
    label: 'USDC on Ethereum',
  },
]

const EU_VIRTUAL_ACCOUNT_TOKENS: {
  token: IAddress
  chainId: number
  label: string
}[] = [
  {
    token: BASE_EURC_ADDRESS,
    chainId: base.id,
    label: 'EURC on Base',
  },
]

export const VIRTUAL_ACCOUNT_CHAIN_IDS = uniq(
  [...US_VIRTUAL_ACCOUNT_TOKENS, ...EU_VIRTUAL_ACCOUNT_TOKENS].map(
    ({ chainId }) => chainId,
  ),
)

export const __testing__ = {
  VirtualAccountForm,
}
